<script setup>
import { ref, computed, onMounted } from "vue";
import { apiQuery } from "@/api/j24api";
import { fetchAuthSession } from "aws-amplify/auth";

const currentStep = ref(1);
const loading = ref(true);
const validating = ref(false);
const submitting = ref(false);
const isAdmin = ref(false);
const author = ref("");
const templates = ref([]);
const products = ref([]);
const productionProducts = ref([]);
const allowContinue = ref(true);
const bothSelected = ref(false);

const dependencies = ref({ dependencies: [], block: false });
const requestDependencies = ref({ dependencies: [], block: false });
const deleteDependencies = ref({ dependencies: [], block: false });
const productDependencies = ref({ dependencies: [], block: false });

const showCompletionDialog = ref(false);
const showCompletionWarningDialog = ref(false);

const objectTypeDisplayNames = {
  policies: "Policies",
  scripts: "Scripts",
  packages: "Packages",
  computergroups: "Computer Groups",
  mobiledevicegroups: "Mobile Groups",
  osxconfigurationprofiles: "macOS Configuration Profiles",
  mobiledeviceconfigurationprofiles: "Mobile Configuration Profiles",
  computerextensionattributes: "macOS Extension Attributes",
  mobiledeviceextensionattributes: "Mobile Extension Attributes",
  categories: "Categories",
  dockitems: "Dock Items",
  diskencryptionconfigurations: "Disk Encryption Configurations",
  restrictedsoftware: "Restricted Software",
  advancedcomputersearches: "Advanced Computer Searches",
  advancedmobiledevicesearches: "Advanced Mobile Searches",
  networksegments: "Network Segments",
  "/v3/computer-prestages": "Computer Prestages",
  "/v2/mobile-device-prestages": "Mobile Device Prestages",
};

const steps = [
  { title: "Select Staged Items", value: 1, complete: false },
  { title: "Confirm Changes", value: 2, complete: false },
];

const templateHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Type", value: "typeDisplay", sortable: true },
  { title: "Author", value: "author", sortable: true },
  { title: "Status", value: "statusDisplay" },
  { title: "Authorize", value: "authoriseToProduction", width: "100px" },
  { title: "Request", value: "requestMoveToProduction", width: "100px" },
  { title: "Delete", value: "requestDelete", width: "80px" },
];

const productHeaders = [
  { title: "Name", value: "name", sortable: true },
  { title: "Description", value: "description", sortable: false },
  { title: "Author", value: "author", sortable: true },
  { title: "Status", value: "statusDisplay" },
  { title: "Authorize", value: "authoriseToProduction", width: "100px" },
  { title: "Request", value: "requestMoveToProduction", width: "100px" },
  { title: "Delete", value: "requestDelete", width: "80px" },
];

const itemsPerPage = [
  { value: 25, title: "25" },
  { value: 50, title: "50" },
  { value: -1, title: "All" },
];

async function getPortalUser() {
  try {
    const session = await fetchAuthSession();
    return session.tokens.idToken.payload.email;
  } catch {
    return "Unknown";
  }
}

onMounted(async () => {
  loading.value = true;

  author.value = await getPortalUser();

  // Check if user is a template admin
  const adminResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "isTemplateAdmin",
  });
  isAdmin.value = adminResult === true;

  // Get user's own staged items
  const stagedResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getStagedItems",
    author: author.value,
  });

  let stagedItems = [];
  if (typeof stagedResult === "object" && Array.isArray(stagedResult)) {
    stagedItems = stagedResult;
  } else if (typeof stagedResult === "string") {
    try {
      stagedItems = JSON.parse(stagedResult);
    } catch {
      stagedItems = [];
    }
  }

  // Add UI action flags to each template
  stagedItems = stagedItems.map((item) => ({
    ...item,
    typeDisplay: objectTypeDisplayNames[item.type] || item.type,
    statusDisplay: item.moveToProd ? "Awaiting Approval" : "Staged",
    authoriseToProduction: false,
    requestMoveToProduction: false,
    requestDelete: false,
  }));

  // If admin, also fetch items awaiting approval from other authors
  if (isAdmin.value) {
    const awaitingResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "getStagedItemsAwaitingApproval",
      author: author.value,
    });

    let awaitingItems = [];
    if (typeof awaitingResult === "object" && Array.isArray(awaitingResult)) {
      awaitingItems = awaitingResult;
    } else if (typeof awaitingResult === "string") {
      try {
        awaitingItems = JSON.parse(awaitingResult);
      } catch {
        awaitingItems = [];
      }
    }

    awaitingItems = awaitingItems.map((item) => ({
      ...item,
      typeDisplay: objectTypeDisplayNames[item.type] || item.type,
      statusDisplay: "Awaiting Approval",
      authoriseToProduction: false,
      requestMoveToProduction: false,
      requestDelete: false,
    }));

    stagedItems.push(...awaitingItems);
  }

  templates.value = stagedItems;

  // Fetch staged products
  const stagedProductsResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getStagedProducts",
    author: author.value,
  });

  let stagedProducts = [];
  if (
    typeof stagedProductsResult === "object" &&
    Array.isArray(stagedProductsResult)
  ) {
    stagedProducts = stagedProductsResult;
  } else if (typeof stagedProductsResult === "string") {
    try {
      stagedProducts = JSON.parse(stagedProductsResult);
    } catch {
      stagedProducts = [];
    }
  }

  stagedProducts = stagedProducts.map((p) => ({
    ...p,
    statusDisplay: p.moveToProd ? "Awaiting Approval" : "Staged",
    authoriseToProduction: false,
    requestMoveToProduction: false,
    requestDelete: false,
  }));

  if (isAdmin.value) {
    const awaitingProductsResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "getStagedProductsAwaitingApproval",
      author: author.value,
    });

    let awaitingProducts = [];
    if (
      typeof awaitingProductsResult === "object" &&
      Array.isArray(awaitingProductsResult)
    ) {
      awaitingProducts = awaitingProductsResult;
    } else if (typeof awaitingProductsResult === "string") {
      try {
        awaitingProducts = JSON.parse(awaitingProductsResult);
      } catch {
        awaitingProducts = [];
      }
    }

    awaitingProducts = awaitingProducts.map((p) => ({
      ...p,
      statusDisplay: "Awaiting Approval",
      authoriseToProduction: false,
      requestMoveToProduction: false,
      requestDelete: false,
    }));

    stagedProducts.push(...awaitingProducts);
  }

  products.value = stagedProducts;

  // Fetch production products for overwrite detection
  const prodResult = await apiQuery("ELA", "mdmTools/command", {
    mdm: "jamf",
    command: "getProducts",
  });
  if (Array.isArray(prodResult)) {
    productionProducts.value = prodResult;
  }

  loading.value = false;
});

function canAuthorise(item) {
  return isAdmin.value && item.moveToProd && item.author !== author.value;
}

function canRequest(item) {
  return item.author === author.value && !item.moveToProd;
}

function canDelete(item) {
  return item.author === author.value;
}

function onActionChange(item, action) {
  // Clear other actions when one is selected
  if (action === "authorise" && item.authoriseToProduction) {
    item.requestMoveToProduction = false;
    item.requestDelete = false;
  } else if (action === "request" && item.requestMoveToProduction) {
    item.authoriseToProduction = false;
    item.requestDelete = false;
  } else if (action === "delete" && item.requestDelete) {
    item.authoriseToProduction = false;
    item.requestMoveToProduction = false;
  }
}

const hasAnySelection = computed(() => {
  const hasTemplateSelection = templates.value.some(
    (t) =>
      t.authoriseToProduction || t.requestMoveToProduction || t.requestDelete
  );
  const hasProductSelection = products.value.some(
    (p) =>
      p.authoriseToProduction || p.requestMoveToProduction || p.requestDelete
  );
  return hasTemplateSelection || hasProductSelection;
});

const authoriseTemplates = computed(() => {
  return templates.value.filter((t) => t.authoriseToProduction);
});

const requestTemplates = computed(() => {
  return templates.value.filter((t) => t.requestMoveToProduction);
});

const deleteTemplates = computed(() => {
  return templates.value.filter((t) => t.requestDelete);
});

const authoriseProducts = computed(() => {
  return products.value.filter((p) => p.authoriseToProduction);
});

const requestProducts = computed(() => {
  return products.value.filter((p) => p.requestMoveToProduction);
});

const deleteProducts = computed(() => {
  return products.value.filter((p) => p.requestDelete);
});

const overwriteProducts = computed(() => {
  const prodNames = productionProducts.value.map((p) => p.name);
  return authoriseProducts.value.filter((p) => prodNames.includes(p.name));
});

async function validateAndContinue() {
  validating.value = true;
  allowContinue.value = true;
  bothSelected.value = false;

  // Validate no item has multiple actions (templates)
  for (const t of templates.value) {
    let count = 0;
    if (t.authoriseToProduction) count++;
    if (t.requestMoveToProduction) count++;
    if (t.requestDelete) count++;
    if (count > 1) {
      bothSelected.value = true;
      allowContinue.value = false;
      validating.value = false;
      return;
    }
  }

  // Validate no product has multiple actions
  for (const p of products.value) {
    let count = 0;
    if (p.authoriseToProduction) count++;
    if (p.requestMoveToProduction) count++;
    if (p.requestDelete) count++;
    if (count > 1) {
      bothSelected.value = true;
      allowContinue.value = false;
      validating.value = false;
      return;
    }
  }

  // Check dependencies for templates being authorised to production
  if (authoriseTemplates.value.length > 0) {
    const depResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "checkStagedDependencies",
      templates: authoriseTemplates.value.map((t) => ({
        name: t.name,
        type: t.type,
      })),
      author: authoriseTemplates.value[0].author,
    });
    dependencies.value =
      typeof depResult === "string" ? JSON.parse(depResult) : depResult;
    if (dependencies.value.block) {
      allowContinue.value = false;
    }
  } else {
    dependencies.value = { dependencies: [], block: false };
  }

  // Check dependencies for templates being requested for promotion
  if (requestTemplates.value.length > 0) {
    const reqDepResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "checkStagedDependencies",
      templates: requestTemplates.value.map((t) => ({
        name: t.name,
        type: t.type,
      })),
      author: author.value,
    });
    requestDependencies.value =
      typeof reqDepResult === "string"
        ? JSON.parse(reqDepResult)
        : reqDepResult;
    if (requestDependencies.value.block) {
      allowContinue.value = false;
    }
  } else {
    requestDependencies.value = { dependencies: [], block: false };
  }

  // Check dependencies for templates being deleted
  if (deleteTemplates.value.length > 0) {
    const delDepResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "checkStagedDependencies",
      templates: deleteTemplates.value.map((t) => ({
        name: t.name,
        type: t.type,
      })),
      author: author.value,
    });
    deleteDependencies.value =
      typeof delDepResult === "string"
        ? JSON.parse(delDepResult)
        : delDepResult;
  } else {
    deleteDependencies.value = { dependencies: [], block: false };
  }

  // Check dependencies for products being authorised to production
  if (authoriseProducts.value.length > 0) {
    const prodDepResult = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "checkStagedProductDependencies",
      products: authoriseProducts.value.map((p) => ({ name: p.name })),
      author: authoriseProducts.value[0].author,
    });
    productDependencies.value =
      typeof prodDepResult === "string"
        ? JSON.parse(prodDepResult)
        : prodDepResult;
    if (productDependencies.value.block) {
      allowContinue.value = false;
    }
  } else {
    productDependencies.value = { dependencies: [], block: false };
  }

  validating.value = false;
  steps[0].complete = true;
  currentStep.value = 2;
}

async function submitChanges() {
  if (!allowContinue.value) return;
  submitting.value = true;

  let hasErrors = false;

  // 1. Move dependencies to production (staged ones)
  for (const dep of dependencies.value.dependencies) {
    if (dep.staged && dep.import) {
      const result = await apiQuery("ELA", "mdmTools/command", {
        mdm: "jamf",
        command: "moveToProduction",
        name: dep.name,
        type: dep.type,
        author: dep.author,
        adminEmail: author.value,
      });
      if (
        result !== "Complete" &&
        (!result || result.body !== undefined
          ? result?.body !== "Complete"
          : true)
      ) {
        hasErrors = true;
      }
    }
  }

  // 2. Authorise templates to production
  for (const t of authoriseTemplates.value) {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "moveToProduction",
      name: t.name,
      type: t.type,
      author: t.author,
      adminEmail: author.value,
    });
    if (
      result !== "Complete" &&
      (!result || result.body !== undefined
        ? result?.body !== "Complete"
        : true)
    ) {
      hasErrors = true;
    }
  }

  // 3. Request move to production
  for (const t of requestTemplates.value) {
    await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "requestMoveToProduction",
      name: t.name,
      type: t.type,
      author: author.value,
    });
  }

  // 4. Request dependencies for move
  for (const dep of requestDependencies.value.dependencies) {
    if (dep.import) {
      await apiQuery("ELA", "mdmTools/command", {
        mdm: "jamf",
        command: "requestMoveToProduction",
        name: dep.name,
        type: dep.type,
        author: author.value,
      });
    }
  }

  // 5. Delete staged items
  const templatesToDelete = [
    ...deleteTemplates.value.map((t) => ({ name: t.name, type: t.type })),
    ...deleteDependencies.value.dependencies
      .filter((dep) => dep.import)
      .map((dep) => ({ name: dep.name, type: dep.type })),
  ];

  if (templatesToDelete.length > 0) {
    await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "deleteStagedItems",
      templates: templatesToDelete,
      author: author.value,
    });
  }

  // 6. Authorise products to production
  for (const p of authoriseProducts.value) {
    const result = await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "moveProductToProduction",
      name: p.name,
      author: p.author,
      adminEmail: author.value,
    });
    if (
      result !== "Complete" &&
      (!result || result.body !== undefined
        ? result?.body !== "Complete"
        : true)
    ) {
      hasErrors = true;
    }
  }

  // 7. Request products move to production
  for (const p of requestProducts.value) {
    await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "requestProductMoveToProduction",
      name: p.name,
      author: author.value,
    });
  }

  // 8. Delete staged products
  const productsToDelete = deleteProducts.value.map((p) => ({
    name: p.name,
  }));

  if (productsToDelete.length > 0) {
    await apiQuery("ELA", "mdmTools/command", {
      mdm: "jamf",
      command: "deleteStagedProducts",
      products: productsToDelete,
      author: author.value,
    });
  }

  submitting.value = false;
  steps[1].complete = true;

  if (hasErrors) {
    showCompletionWarningDialog.value = true;
  } else {
    showCompletionDialog.value = true;
  }
}

function prevStep() {
  if (currentStep.value > 1) {
    currentStep.value--;
    steps[currentStep.value - 1].complete = false;
  }
}
</script>

<template>
  <v-stepper v-model="currentStep">
    <v-stepper-header>
      <v-stepper-item
        v-for="(step, index) in steps"
        :key="index"
        :title="step.title"
        :value="step.value"
        :complete="step.complete"
      ></v-stepper-item>
    </v-stepper-header>
    <v-stepper-content
      v-for="(step, index) in steps"
      :key="index"
      :step="step.value"
    >
      <v-sheet v-if="step.value === currentStep">
        <v-card-text>
          <!-- Step 1: Select Staged Items -->
          <template v-if="step.value === 1">
            <h2>Staged Templates</h2>
            <p class="text-body-2 mb-4">
              Select an action for each staged template. You can only select one
              action per item.
            </p>

            <v-alert v-if="bothSelected" type="error" class="mb-4">
              You cannot select multiple actions for the same item.
            </v-alert>

            <v-data-table
              :headers="templateHeaders"
              :items="templates"
              item-key="_id"
              class="elevation-2"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              :loading="loading"
            >
              <template #[`item.statusDisplay`]="{ item }">
                <v-chip
                  :color="
                    item.statusDisplay === 'Awaiting Approval'
                      ? 'orange'
                      : 'blue'
                  "
                  size="small"
                >
                  {{ item.statusDisplay }}
                </v-chip>
              </template>
              <template #[`item.authoriseToProduction`]="{ item }">
                <v-checkbox-btn
                  v-if="canAuthorise(item)"
                  v-model="item.authoriseToProduction"
                  @update:model-value="onActionChange(item, 'authorise')"
                  color="success"
                  hide-details
                ></v-checkbox-btn>
              </template>
              <template #[`item.requestMoveToProduction`]="{ item }">
                <v-checkbox-btn
                  v-if="canRequest(item)"
                  v-model="item.requestMoveToProduction"
                  @update:model-value="onActionChange(item, 'request')"
                  color="primary"
                  hide-details
                ></v-checkbox-btn>
              </template>
              <template #[`item.requestDelete`]="{ item }">
                <v-checkbox-btn
                  v-if="canDelete(item)"
                  v-model="item.requestDelete"
                  @update:model-value="onActionChange(item, 'delete')"
                  color="error"
                  hide-details
                ></v-checkbox-btn>
              </template>
            </v-data-table>

            <div
              v-if="templates.length === 0 && !loading"
              class="text-center py-8"
            >
              <v-icon size="64" color="grey">mdi-inbox-outline</v-icon>
              <p class="mt-4 text-grey">No staged templates found.</p>
            </div>

            <h2 class="mt-8">Staged Products</h2>
            <p class="text-body-2 mb-4">
              Select an action for each staged product. You can only select one
              action per item.
            </p>

            <v-data-table
              :headers="productHeaders"
              :items="products"
              item-key="_id"
              class="elevation-2"
              :items-per-page="25"
              :items-per-page-options="itemsPerPage"
              :loading="loading"
            >
              <template #[`item.statusDisplay`]="{ item }">
                <v-chip
                  :color="
                    item.statusDisplay === 'Awaiting Approval'
                      ? 'orange'
                      : 'blue'
                  "
                  size="small"
                >
                  {{ item.statusDisplay }}
                </v-chip>
              </template>
              <template #[`item.authoriseToProduction`]="{ item }">
                <v-checkbox-btn
                  v-if="canAuthorise(item)"
                  v-model="item.authoriseToProduction"
                  @update:model-value="onActionChange(item, 'authorise')"
                  color="success"
                  hide-details
                ></v-checkbox-btn>
              </template>
              <template #[`item.requestMoveToProduction`]="{ item }">
                <v-checkbox-btn
                  v-if="canRequest(item)"
                  v-model="item.requestMoveToProduction"
                  @update:model-value="onActionChange(item, 'request')"
                  color="primary"
                  hide-details
                ></v-checkbox-btn>
              </template>
              <template #[`item.requestDelete`]="{ item }">
                <v-checkbox-btn
                  v-if="canDelete(item)"
                  v-model="item.requestDelete"
                  @update:model-value="onActionChange(item, 'delete')"
                  color="error"
                  hide-details
                ></v-checkbox-btn>
              </template>
            </v-data-table>

            <div
              v-if="products.length === 0 && !loading"
              class="text-center py-8"
            >
              <v-icon size="64" color="grey">mdi-inbox-outline</v-icon>
              <p class="mt-4 text-grey">No staged products found.</p>
            </div>
          </template>

          <!-- Step 2: Confirm Changes -->
          <template v-if="step.value === 2">
            <h2>Confirm Changes</h2>

            <!-- Authorise to Production -->
            <template v-if="authoriseTemplates.length > 0">
              <h3 class="mt-4 mb-2">Templates to Authorise to Production</h3>
              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Author</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in authoriseTemplates" :key="t._id">
                    <td>{{ t.name }}</td>
                    <td>{{ t.typeDisplay }}</td>
                    <td>{{ t.author }}</td>
                  </tr>
                </tbody>
              </v-table>

              <template
                v-if="
                  dependencies.dependencies &&
                  dependencies.dependencies.length > 0
                "
              >
                <h4 class="mb-2">Dependencies to also promote</h4>
                <v-table density="compact" class="elevation-1 mb-4">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>In Staging</th>
                      <th>In Production</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(dep, idx) in dependencies.dependencies"
                      :key="idx"
                      :class="{
                        'bg-red-lighten-5':
                          !dep.staged && !dep.existsInProduction,
                      }"
                    >
                      <td>{{ dep.name }}</td>
                      <td>
                        {{ objectTypeDisplayNames[dep.type] || dep.type }}
                      </td>
                      <td>
                        <v-icon
                          :color="dep.staged ? 'success' : 'error'"
                          size="small"
                        >
                          {{ dep.staged ? "mdi-check" : "mdi-close" }}
                        </v-icon>
                      </td>
                      <td>
                        <v-icon
                          :color="dep.existsInProduction ? 'success' : 'error'"
                          size="small"
                        >
                          {{
                            dep.existsInProduction ? "mdi-check" : "mdi-close"
                          }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>

              <v-alert v-if="dependencies.block" type="error" class="mb-4">
                Cannot proceed: one or more dependencies are missing from both
                staging and production.
              </v-alert>
            </template>

            <!-- Request Move to Production -->
            <template v-if="requestTemplates.length > 0">
              <h3 class="mt-4 mb-2">Templates to Request Move to Production</h3>
              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in requestTemplates" :key="t._id">
                    <td>{{ t.name }}</td>
                    <td>{{ t.typeDisplay }}</td>
                  </tr>
                </tbody>
              </v-table>

              <template
                v-if="
                  requestDependencies.dependencies &&
                  requestDependencies.dependencies.length > 0
                "
              >
                <h4 class="mb-2">Dependencies to also request for promotion</h4>
                <v-table density="compact" class="elevation-1 mb-4">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>In Staging</th>
                      <th>In Production</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(dep, idx) in requestDependencies.dependencies"
                      :key="idx"
                      :class="{
                        'bg-red-lighten-5':
                          !dep.staged && !dep.existsInProduction,
                      }"
                    >
                      <td>{{ dep.name }}</td>
                      <td>
                        {{ objectTypeDisplayNames[dep.type] || dep.type }}
                      </td>
                      <td>
                        <v-icon
                          :color="dep.staged ? 'success' : 'error'"
                          size="small"
                        >
                          {{ dep.staged ? "mdi-check" : "mdi-close" }}
                        </v-icon>
                      </td>
                      <td>
                        <v-icon
                          :color="dep.existsInProduction ? 'success' : 'error'"
                          size="small"
                        >
                          {{
                            dep.existsInProduction ? "mdi-check" : "mdi-close"
                          }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>

              <v-alert
                v-if="requestDependencies.block"
                type="error"
                class="mb-4"
              >
                Cannot proceed: one or more dependencies are missing from both
                staging and production.
              </v-alert>
            </template>

            <!-- Delete -->
            <template v-if="deleteTemplates.length > 0">
              <h3 class="mt-4 mb-2">Templates to Delete</h3>
              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in deleteTemplates" :key="t._id">
                    <td>{{ t.name }}</td>
                    <td>{{ t.typeDisplay }}</td>
                  </tr>
                </tbody>
              </v-table>

              <template
                v-if="
                  deleteDependencies.dependencies &&
                  deleteDependencies.dependencies.length > 0
                "
              >
                <h4 class="mb-2">Staged dependencies</h4>
                <v-table density="compact" class="elevation-1 mb-4">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>In Staging</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(dep, idx) in deleteDependencies.dependencies"
                      :key="idx"
                    >
                      <td>{{ dep.name }}</td>
                      <td>
                        {{ objectTypeDisplayNames[dep.type] || dep.type }}
                      </td>
                      <td>
                        <v-icon
                          :color="dep.staged ? 'success' : 'grey'"
                          size="small"
                        >
                          {{ dep.staged ? "mdi-check" : "mdi-minus" }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </template>

            <!-- Products to Authorise -->
            <template v-if="authoriseProducts.length > 0">
              <h3 class="mt-4 mb-2">Products to Authorise to Production</h3>

              <v-alert
                v-if="overwriteProducts.length > 0"
                type="warning"
                class="mb-4"
              >
                The following product{{
                  overwriteProducts.length > 1 ? "s" : ""
                }}
                already exist in production and will be overwritten:
                <strong>{{
                  overwriteProducts.map((p) => p.name).join(", ")
                }}</strong>
              </v-alert>

              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Author</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in authoriseProducts" :key="p._id">
                    <td>{{ p.name }}</td>
                    <td>{{ p.description }}</td>
                    <td>{{ p.author }}</td>
                  </tr>
                </tbody>
              </v-table>

              <template
                v-if="
                  productDependencies.dependencies &&
                  productDependencies.dependencies.length > 0
                "
              >
                <h4 class="mb-2">Template dependencies</h4>
                <v-table density="compact" class="elevation-1 mb-4">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>In Staging</th>
                      <th>In Production</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(dep, idx) in productDependencies.dependencies"
                      :key="idx"
                      :class="{
                        'bg-red-lighten-5':
                          !dep.staged && !dep.existsInProduction,
                      }"
                    >
                      <td>{{ dep.name }}</td>
                      <td>
                        {{ objectTypeDisplayNames[dep.type] || dep.type }}
                      </td>
                      <td>
                        <v-icon
                          :color="dep.staged ? 'success' : 'error'"
                          size="small"
                        >
                          {{ dep.staged ? "mdi-check" : "mdi-close" }}
                        </v-icon>
                      </td>
                      <td>
                        <v-icon
                          :color="dep.existsInProduction ? 'success' : 'error'"
                          size="small"
                        >
                          {{
                            dep.existsInProduction ? "mdi-check" : "mdi-close"
                          }}
                        </v-icon>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </template>

              <v-alert
                v-if="productDependencies.block"
                type="error"
                class="mb-4"
              >
                Cannot proceed: one or more template dependencies referenced by
                a product are missing from both staging and production.
              </v-alert>
            </template>

            <!-- Products to Request Move -->
            <template v-if="requestProducts.length > 0">
              <h3 class="mt-4 mb-2">Products to Request Move to Production</h3>
              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in requestProducts" :key="p._id">
                    <td>{{ p.name }}</td>
                    <td>{{ p.description }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <!-- Products to Delete -->
            <template v-if="deleteProducts.length > 0">
              <h3 class="mt-4 mb-2">Products to Delete</h3>
              <v-table density="compact" class="elevation-1 mb-4">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in deleteProducts" :key="p._id">
                    <td>{{ p.name }}</td>
                    <td>{{ p.description }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>

            <div
              v-if="
                authoriseTemplates.length === 0 &&
                requestTemplates.length === 0 &&
                deleteTemplates.length === 0 &&
                authoriseProducts.length === 0 &&
                requestProducts.length === 0 &&
                deleteProducts.length === 0
              "
              class="text-center py-8"
            >
              <p class="text-grey">No changes selected.</p>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-btn
            v-if="currentStep > 1"
            variant="elevated"
            @click="prevStep"
            class="mr-auto"
            color="rgb(247, 247, 247)"
          >
            Back
          </v-btn>
          <v-btn
            v-if="currentStep === 1"
            variant="elevated"
            @click="validateAndContinue"
            color="primary"
            :loading="validating"
            :disabled="!hasAnySelection || validating"
          >
            Next
          </v-btn>
          <v-btn
            v-if="currentStep === 2"
            @click="submitChanges"
            variant="elevated"
            color="primary"
            :loading="submitting"
            :disabled="submitting || !allowContinue"
          >
            Submit Changes
          </v-btn>
        </v-card-actions>
      </v-sheet>

      <!-- Completion Dialog -->
      <v-dialog v-model="showCompletionDialog" max-width="500" persistent>
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="success"
                icon="mdi-check-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Changes Applied</div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>

      <v-dialog
        v-model="showCompletionWarningDialog"
        max-width="500"
        persistent
      >
        <div class="text-center">
          <v-card>
            <div class="py-12 text-center">
              <v-icon
                class="mb-6"
                color="warning"
                icon="mdi-alert-circle-outline"
                size="128"
              ></v-icon>
              <div class="text-h4 font-weight-bold">Completed with Errors</div>
              <div style="padding: 0 20px">
                Some changes may not have been applied. Please check and try
                again.
              </div>
            </div>
            <v-divider></v-divider>
            <div class="pa-4 text-center">
              <v-btn
                color="medium-emphasis"
                min-width="200"
                variant="outlined"
                @click="showCompletionWarningDialog = false"
              >
                Close
              </v-btn>
            </div>
          </v-card>
        </div>
      </v-dialog>
    </v-stepper-content>
  </v-stepper>
</template>
